import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import { isAfter } from 'date-fns';
import classNames from 'classnames';

import './CancelTicket.scss';
import Button from 'src/components/Button/Button';
import { useHistory } from 'react-router-dom';
import { RootState } from 'src/configs/store';
import { formatAmountToFixed } from 'src/utils/utils';

interface CancelTicketProps {
  setDetails?: Function,
  isCard?: boolean,
  isCancel?: boolean,
  isPayout?:boolean
  isCancelRequest?: boolean,
  updateIsPayotable: Function,
  updateIsCancellable: Function
}

function CancelTicket({
  setDetails = () => {},
  isCard=false,
  isCancel=false,
  isPayout=false,
  isCancelRequest=false,
  updateIsCancellable,
  updateIsPayotable
}: CancelTicketProps) {
  const { t } = useTranslation();
  const [ticket, setTitle] = useState('');
  const [pin, setPin] = useState('');
  const dispatch = useDispatch();
  const [ticketCode, setTicketCode] = useState('');
  const [reason, setReason] = useState('');
  const { ticketError,
    pinError,
    showTicketScanOnly,
    showTicketCodeNoPin,
    showTicketCodeAndPin
  } = useSelector((state: RootState) => state.peripherals);

  const history = useHistory();

  const checkPinError = () => {
    dispatch.peripherals.changePinError((/^\d+$/.test(pin) && pin.length !== 4));
  };

  useEffect(() => {
    checkPinError();

    return (() => {
      dispatch.peripherals.changeTicketError(false);
      dispatch.peripherals.changePinError(false);
    });
  }, [pin]);

  const getTicketDetails = async () => {
    const details = await dispatch.peripherals.getTicketDetails({ ticket, pin });

    if (details && _.size(details) > 0 && !isCancel && !isPayout && details !== 'pinError'){
      updateIsCancellable(details.isCancellable && isAfter(new Date(), details.cancellableUntil));
      updateIsPayotable(details.statusName === 'settled' && details.netReturn > 0 && details.paidOut < details.netReturn);
      setDetails(details);
    } else if (details === 'pinError') {
      dispatch.peripherals.changePinError(true);
    } else if (details && _.size(details) > 0 && isCancel && details !== 'pinError') {
      history.push(`#cancel-ticket?&code=${ details.code }`);
    } else if (details && _.size(details) > 0 && isPayout && details !== 'pinError'){
      const value = details.netReturn - details.paidOut;
      history.replace(`#payout-ticket?&code=${ details.code }&value=${ formatAmountToFixed(value, details.divisor, true) }`);
    } else {
      dispatch.peripherals.changeTicketError(true);
    }
  };

  const handleCancelRequest = async (e) => {
    e.preventDefault();
    const response = await dispatch.peripherals.cancelRequest({ ticketCode, reason });
    if (response){
      history.push('#cancel-request');
    }
  };

  const checkOnClick = async (e) => {
    if (e){
      e.preventDefault();
    }
    dispatch.peripherals.setClicked(true);
    if (isCancel && pin === ''){
      history.replace(`#cancel-ticket?&code=${ ticket }&pin=${ pin }`);
    } else if (isPayout && pin === ''){
      history.replace(`#payout-ticket?&code=${ ticket }&pin=${ pin }`);
    } else if (isCancelRequest){
      handleCancelRequest(e);
    } else {
      getTicketDetails();
    }
  };

  const handleInput = (e) => {
    if (e.target.name === 'ticket'){
      setTitle(e.target.value);
      dispatch.peripherals.changeTicketError(false);
    } else {
      setPin(e.target.value);
      dispatch.peripherals.changePinError(false);
    }
  };

  return (

    <div className="cancel-ticket">
      {
        !isCancelRequest &&
        <div>
          <h2 className="cancel-ticket__title">{ t('form.ticketInformation') }</h2>
          <form onSubmit={ checkOnClick }>
            <div className="cancel-ticket__form">
              <label className="cancel-ticket__label">{ t('form.ticket') }</label>
              <input
                type="text"
                autoComplete="new-password"
                name="ticket"
                disabled={ showTicketScanOnly === 1 && localStorage.getItem('fakeTill') !== 'true' }
                className={
                  classNames({
                    'cancel-ticket__inputs' : true,
                    'cancel-ticket__input--error': ticketError
                  })
                }
                onChange={ handleInput }
              />
              {
                ticketError &&
            <div className="cancel-ticket__error">{ t('form.invalidTicket') }</div>
              }
            </div>
            <div className="cancel-ticket__form">
              <label className="cancel-ticket__label">{ isCard ? t('ticketDetails.customerNumber') : t('ticketDetails.pin') }</label>
              <input
                type="text"
                autoComplete="new-password"
                disabled={ (showTicketScanOnly === 1 || showTicketCodeNoPin === 1) && localStorage.getItem('fakeTill') !== 'true' }
                className={
                  classNames({
                    'cancel-ticket__inputs' : true,
                    'cancel-ticket__input--error': pinError || pin.length > 4
                  })
                }
                onChange={ handleInput }
              />
              { pinError && <div className="cancel-ticket__error">{ t('form.pinError') }</div> }
            </div>
            <div className="cancel-ticket__buttons-container">
              <Button
                type="submit"
                text={ t('retail.submit') }
                disabled={
                  ((pinError || pin.length < 4 || pin.length > 4) && showTicketCodeAndPin === 1)
                }
                className="btn btn--outlined cancel-ticket__submit retail-home__button--revert-color"
                onClick={ checkOnClick }
              />
            </div>
          </form>
        </div>
      }
      {
        isCancelRequest && <div>
          <h2 className="cancel-ticket__title">{ t('form.ticketCancelRequest') }</h2>
          <form onSubmit={ handleCancelRequest }>
            <div className="cancel-ticket__form">
              <label className="cancel-ticket__label">{ t('form.ticket') }</label>
              <input
                type="text"
                autoComplete="new-password"
                name="ticket"
                className="cancel-ticket__inputs"
                onChange={ (e) => setTicketCode(e.target.value) }
              />
              {
                ticketError &&
            <div className="cancel-ticket__error">{ t('form.invalidTicket') }</div>
              }
            </div>
            <div className="cancel-ticket__form">
              <label className="cancel-ticket__label">{ t('form.ticketReason') }</label>
              <input
                type="text"
                autoComplete="new-password"
                className="cancel-ticket__inputs"
                onChange={ (e) => setReason(e.target.value) }
              />
            </div>
            <div className="cancel-ticket__buttons-container">
              <Button
                type="submit"
                onClick={ handleCancelRequest }
                text={ t('retail.submit') }
                className="btn btn--outlined cancel-ticket__submit retail-home__button--revert-color"
              />
            </div>
          </form>
        </div>
      }
    </div>
  );
}

export default CancelTicket;
